import React, { useState, useEffect } from 'react'
import { Clock, MapPin, Users, Star } from 'lucide-react'
import { brandCSS } from '../../brand'
import { CRUISE_CSS } from './cruise.css.js'
import { LIGHTBOX_CSS } from '../components/lightbox.css.js'
import { BOOKING_MODAL_CSS } from '../components/bookingModal.css.js'
import Reveal from '../components/Reveal'
import Hero from './Hero'
import BookingModal from '../components/BookingModal.jsx'
import CabinTabBar, { CABIN_TABS } from './TabBar'
import Overview from './Overview.jsx'
import Routemap from '../components/Routemap.jsx'
import Cabins from './tabs/Cabins'
import DeckPlan from './tabs/DeckPlan'
import Itineraries from './Itineraries.jsx'
import Activities from './Activities'
import Gallery from './Gallery'
import CTASection from '../components/CTASection.jsx'
import BookingSearchForm from '../components/BookingSearchForm.jsx'

function acf(data, key, fallback) {
  return data?.acf?.[key] ?? fallback
}

export default function SingleCruise({ data }) {
  useEffect(() => {
    const root = document.getElementById('app-root')
    if (root) root.style.visibility = 'visible'
  }, [])
  
  const [cabinTab, setCabinTab] = useState(CABIN_TABS[0])
  const [bookingOpen, setBookingOpen] = useState(false)
  const post = data?.post

  const meta = [
    { icon: Clock,  label: acf(data, 'duration',  '2–3 Nights') },
    { icon: MapPin, label: acf(data, 'departure', 'Halong Bay') },
    { icon: Users,  label: acf(data, 'guests',    '27 Cabins') },
    { icon: Star,   label: `${acf(data, 'rating', '4.9')} Rating` },
  ]

  return (
    <>
      <style>{brandCSS + LIGHTBOX_CSS + BOOKING_MODAL_CSS + CRUISE_CSS}</style>
      <div className="sc-page">

        {/* ① Hero — full screen */}
        <Hero
          title={post?.title ?? 'Le Journey Premier'}
          thumb={post?.thumb}
          tagline={acf(data, 'tagline', 'A floating sanctuary on the emerald waters of Halong Bay')}
          meta={meta}
          onBook={() => setBookingOpen(true)}
        />

        <BookingModal
          open={bookingOpen}
          cruiseTitle={post?.title}
          onClose={() => setBookingOpen(false)}
        />

        <BookingSearchForm />

        {/* ② Overview — always visible */}
        <Reveal variant="fade-up" threshold={0.08} as="div" className="sc-main sc-block">
          <Overview
            content={post?.content}
            highlights={acf(data, 'highlights', null)}
            rating={acf(data, 'rating', null)}
            reviewsCount={acf(data, 'reviews', null)}
          />
        </Reveal>

        {/* ③ Routemap — always visible */}
        <Reveal variant="fade-in" threshold={0.1} as="div" className="sc-main sc-block">
          <Routemap
            imageUrl={acf(data, 'routemap_image', null)}
            embedUrl={acf(data, 'routemap_embed', null)}
          />
        </Reveal>

        {/* ⑤ Itineraries — always visible */}
        <Reveal variant="fade-up" threshold={0.06} as="div" className="sc-main sc-block">
          <Itineraries itineraries={acf(data, 'itineraries', null)} />
        </Reveal>

        {/* ④ Cabins + Deck Plan — tabbed block */}
        <Reveal variant="fade-up" threshold={0.06} as="div" className="sc-cabin-block">
          <h2 className="ljn-section-title sc-cabin-block__heading">
            {cabinTab === 'Cabins' ? 'Suites & Cabins' : 'Deck Plan'}
          </h2>
          <CabinTabBar active={cabinTab} onChange={setCabinTab} />
          {cabinTab === 'Cabins'     && <Cabins cabins={acf(data, 'cabins', null)} headingHidden />}
          {cabinTab === 'Deck Plan'  && <DeckPlan deckPlan={acf(data, 'deck_plan', null)} headingHidden />}
        </Reveal>

        {/* ⑥ Activities — always visible */}
        <Reveal variant="fade-up" threshold={0.06} as="div" className="sc-main sc-block">
          <Activities activities={acf(data, 'activities', null)} />
        </Reveal>

        {/* ⑦ Gallery — always visible, full bleed */}
        <Reveal variant="fade-in" threshold={0.05} as="div" className="sc-block">
          <Gallery images={acf(data, 'gallery', null)} />
        </Reveal>

        {/* ⑧ CTA */}
        <CTASection contact={data?.contact} />

      </div>
    </>
  )
}
